<section id="retirement-plans">
  <div class="container">
    <div class="row">
      <div class="col-12">
        <h1>Retirement Plan Services for Catholic Organizations</h1>
        <p class="hero-lead">Fiduciary support and Catholic values screened investment strategies for dioceses, religious institutes, schools, hospitals, universities and other Catholic organizations that sponsor retirement plans.</p>
      </div>
    </div>
  </div>
</section>

<section class="section retirement-page">
  <div class="container">

    <!-- Overview -->
    <div class="row">
      <div class="col-lg-8">
        <h2 class="mt-0">Catholic Retirement Plans Built on Fiduciary Best Practices</h2>
        <p>Investing for Catholics (IFC) retirement plan services and investment strategies are designed to advance the retirement security of employees of Catholic plan sponsors, including religious orders, dioceses, archdioceses, parishes, schools, hospitals, not-for-profit organizations and private companies that want to offer easy-to-own, <a routerLink="/fees">cost-conscious</a>, Catholic values screened target date and risk-based retirement plan strategies.</p>

        <p>IFC's fiduciary services offering provides investment selection and oversight, investment portfolio construction, <a routerLink="/foundations/policy">Investment Policy Statement development</a>, and IFC's Performance Monitoring Report. IFC's <a routerLink="/portfolios/target-date">Catholic Values Target Date Strategies</a> provide easy-to-own, daily-valued, diversified portfolios built to pursue the long-term expected returns of global stock and bond markets, with corresponding risk, in a portfolio screened according to the <a href="https://www.ifa.com/pdfs/revisions-usccb-guidelines-2003-to-2021.pdf" target="_blank" rel="noopener">USCCB Socially Responsible Investment Guidelines</a>. <strong>Catholic Values Investments Rooted in Financial Science.</strong></p>

        <p>Since 2009, we have sat at the table with Catholic plan committees and worked through the same fundamentals together: putting an Investment Policy Statement in writing, following a process that can be repeated and explained, knowing exactly which service provider is responsible for what, checking fees against the market, and holding committee meetings where real questions get asked and answered. None of this is novel or complicated, but in our experience it is often underused across Catholic retirement plans, and closing that gap is where we help.</p>
      </div>

      <div class="col-lg-4">
        <div class="card contact-card">
          <div class="card-body">
            <h3 class="h5 mb-3">Talk With a Retirement Plan Specialist</h3>
            <div class="d-flex align-items-center mb-3">
              <img src="https://services.ifa.com/art/images/1483_512x512_c_mary-brunson-2025_q80.webp" alt="Mary E. Brunson, Co-Founder and Senior Vice President, Investing for Catholics" class="contact-photo me-3" width="96" height="96" loading="lazy">
              <p class="mb-0"><strong>Mary E. Brunson</strong><br>Co-Founder and Senior Vice President</p>
            </div>
            <p class="mb-2">Call <a href="tel:+18888155025" aria-label="Call Investing for Catholics at 888.815.5025">888-815-5025</a></p>
            <p class="mb-3">Email <a href="mailto:mary&#64;ifa.com">mary&#64;ifa.com</a></p>
            <a href="/open-account" class="btn btn-primary w-100">Contact Us</a>
          </div>
        </div>
      </div>
    </div>

    <!-- Resources -->
    <div class="row mt-5">
      <div class="col-12 d-flex flex-wrap justify-content-between align-items-baseline">
        <h2 class="mb-0">Resources for Plan Sponsors</h2>
        <a routerLink="/resources" class="resource-more">View all tools and resources &rsaquo;</a>
      </div>
    </div>
    <div class="row resource-slider">
      <app-banner-slider>
        <app-banner-card [class]="'bg-white h460'" [customBody]="true">
          <a class="d-block resource-cover" href="https://www.ifa.com/pdfs/ifc_brochure.pdf" target="_blank" rel="noopener">
            <img loading="lazy" width="285" height="200" src="https://services.ifa.com/art/images/3646_255x330_c_ifc-brochure-2026_q80.webp" alt="Investing for Catholics Brochure">
          </a>
          <ng-container body>
            <div class="p-3 pt-1">
              <div class="ps-2 pt-0 font-14 text-end text-muted">Brochure</div>
              <div class="pt-2">
                <h3 class="font-open font-18 card-title text-start mb-2"><a href="https://www.ifa.com/pdfs/ifc_brochure.pdf" target="_blank" rel="noopener">Investing for Catholics Brochure</a></h3>
              </div>
              <div class="teaser">Our Catholic values, services, principles and investing strategy, including how we work with retirement plans, foundations and endowments.</div>
              <a class="resource-download" href="https://www.ifa.com/pdfs/ifc_brochure.pdf" target="_blank" rel="noopener">Download PDF &rsaquo;</a>
            </div>
          </ng-container>
        </app-banner-card>

        <app-banner-card [class]="'bg-white h460'" [customBody]="true">
          <a class="d-block resource-cover" href="https://www.ifa.com/pdfs/do-investment-committees-pick-winners.pdf" target="_blank" rel="noopener">
            <img loading="lazy" width="285" height="200" src="images/do-investment-committees-pick-winners.jpg" alt="Do Investment Committees Pick Winners?">
          </a>
          <ng-container body>
            <div class="p-3 pt-1">
              <div class="ps-2 pt-0 font-14 text-end text-muted">Booklet</div>
              <div class="pt-2">
                <h3 class="font-open font-18 card-title text-start mb-2"><a href="https://www.ifa.com/pdfs/do-investment-committees-pick-winners.pdf" target="_blank" rel="noopener">Do Investment Committees Pick Winners?</a></h3>
              </div>
              <div class="teaser">A summary of studies on manager selection, performance chasing and alternative investments for investment committee and finance council members.</div>
              <a class="resource-download" href="https://www.ifa.com/pdfs/do-investment-committees-pick-winners.pdf" target="_blank" rel="noopener">Download PDF &rsaquo;</a>
            </div>
          </ng-container>
        </app-banner-card>

        <app-banner-card [class]="'bg-white h460'" [customBody]="true">
          <a class="d-block resource-cover" href="https://www.ifa.com/pdfs/ifc-target-date-flyer.pdf" target="_blank" rel="noopener">
            <span class="resource-cover-placeholder" aria-hidden="true">
              <span>Catholic Values</span>
              <strong>Target Date<br>Strategies</strong>
            </span>
          </a>
          <ng-container body>
            <div class="p-3 pt-1">
              <div class="ps-2 pt-0 font-14 text-end text-muted">Flyer</div>
              <div class="pt-2">
                <h3 class="font-open font-18 card-title text-start mb-2"><a href="https://www.ifa.com/pdfs/ifc-target-date-flyer.pdf" target="_blank" rel="noopener">Catholic Values Target Date Strategies</a></h3>
              </div>
              <div class="teaser">A one-page overview of IFC Target Date Portfolios for plan sponsors, including how the glide path works and how to add them to a plan.</div>
              <a class="resource-download" href="https://www.ifa.com/pdfs/ifc-target-date-flyer.pdf" target="_blank" rel="noopener">Download Flyer &rsaquo;</a>
            </div>
          </ng-container>
        </app-banner-card>
      </app-banner-slider>
    </div>

    <!-- Who we serve -->
    <div class="row mt-5">
      <div class="col-12">
        <h2>Who We Serve</h2>
        <p>The fiduciary principles are the same for every retirement plan. The practical starting point often is not. IFC works with:</p>
      </div>
      <div class="col-md-6">
        <ul class="check-list">
          <li><strong>Dioceses and archdioceses</strong> sponsoring 403(b), 401(k) or defined benefit plans that cover parish, school, chancery and clergy employees, often through a multiple-employer arrangement.</li>
          <li><strong>Religious institutes and societies of apostolic life</strong> governing lay-employee retirement plans alongside separate arrangements for their own members.</li>
          <li><strong>Catholic parishes and schools</strong> that either participate in a diocesan plan or sponsor their own.</li>
        </ul>
      </div>
      <div class="col-md-6">
        <ul class="check-list">
          <li><strong>Catholic hospitals and health systems</strong> seeking values-aligned retirement plan strategies for their employees.</li>
          <li><strong>Catholic charities, universities and not-for-profit organizations</strong> seeking a values-aligned investment menu.</li>
          <li><strong>Bishops, provincial superiors, finance councils and board members</strong> who hold fiduciary responsibility by virtue of their office.</li>
        </ul>
      </div>
    </div>

    <!-- Services -->
    <div class="row mt-5">
      <div class="col-12">
        <h2>Retirement Plan Services and Investment Strategies</h2>
      </div>
    </div>
    <div class="row g-4">
      <div class="col-md-6 col-lg-4">
        <div class="card service-card h-100">
          <div class="card-body">
            <h3 class="h5">3(38) Investment Manager Services</h3>
            <p>IFC accepts discretionary authority, and the corresponding fiduciary responsibility, to select, monitor and replace plan investments. Plan sponsors retain the duty to prudently select and monitor IFC, and are relieved of much of the day-to-day investment decision-making burden.</p>
          </div>
        </div>
      </div>
      <div class="col-md-6 col-lg-4">
        <div class="card service-card h-100">
          <div class="card-body">
            <h3 class="h5">Catholic Values Screening</h3>
            <p>Plan investment options screened in accordance with the USCCB Socially Responsible Investment Guidelines, with holdings reviewed on an ongoing basis. See <a routerLink="/strategy">how IFC applies Catholic values</a> to its portfolios.</p>
          </div>
        </div>
      </div>
      <div class="col-md-6 col-lg-4">
        <div class="card service-card h-100">
          <div class="card-body">
            <h3 class="h5">Catholic Values Target Date Strategies</h3>
            <p>Daily-valued, diversified <a routerLink="/portfolios/target-date">IFC Target Date Portfolios</a> that automatically become more conservative as participants approach retirement. Designed to serve as a plan's qualified default investment alternative (QDIA), subject to the plan sponsor's determination.</p>
          </div>
        </div>
      </div>
      <div class="col-md-6 col-lg-4">
        <div class="card service-card h-100">
          <div class="card-body">
            <h3 class="h5">Risk-Based Index Portfolios</h3>
            <p><a routerLink="/portfolios">IFC Index Portfolios</a> spanning conservative to aggressive allocations, built from systematically managed, Catholic values screened funds and grounded in decades of academic research.</p>
          </div>
        </div>
      </div>
      <div class="col-md-6 col-lg-4">
        <div class="card service-card h-100">
          <div class="card-body">
            <h3 class="h5">Investment Policy Statement Development</h3>
            <p>A written IPS that defines roles and responsibilities, selection criteria, Catholic values screening methodology, monitoring procedures, the default investment alternative and fee reasonableness standards.</p>
          </div>
        </div>
      </div>
      <div class="col-md-6 col-lg-4">
        <div class="card service-card h-100">
          <div class="card-body">
            <h3 class="h5">Monitoring, Benchmarking and Documentation</h3>
            <p>IFC's Performance Monitoring Report, independent <a routerLink="/fees">fee benchmarking</a>, committee meeting participation and a maintained fiduciary file, so the plan's decisions are documented and can be explained years later.</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Fiduciary basics -->
    <div class="row mt-5">
      <div class="col-md-6">
        <h2>Are You a Fiduciary?</h2>
        <p>Fiduciary status is a matter of function, not title. Depending on the facts, the following roles may be fiduciaries of a Catholic retirement plan:</p>
        <ul>
          <li><strong>The plan sponsor</strong>, typically the diocese, religious institute or Church-affiliated organization and, by extension, its leadership.</li>
          <li><strong>Plan trustees</strong> responsible for managing plan assets and making investment decisions.</li>
          <li><strong>Plan administrators</strong> managing day-to-day operations, benefit payments and recordkeeping.</li>
          <li><strong>Investment committee members</strong> who select and monitor plan investment options.</li>
          <li><strong>Investment advisors</strong> compensated for investment advice, though not every advisor accepts fiduciary status.</li>
          <li><strong>Anyone with discretion</strong> over plan management or plan assets, regardless of formal title.</li>
        </ul>
      </div>
      <div class="col-md-6">
        <h2>What Are a Fiduciary's Duties?</h2>
        <p>ERISA, the U.S. Department of Labor, the IRS and state trust law describe the same core obligations. Fiduciaries are generally required to:</p>
        <ul>
          <li>Act solely in the interest of plan participants and beneficiaries.</li>
          <li>Carry out their duties prudently, following a documented process.</li>
          <li>Follow the plan documents.</li>
          <li>Diversify plan investments.</li>
          <li>Pay only reasonable plan expenses and benchmark them regularly.</li>
          <li>Delegate to prudent experts when internal expertise is lacking, and monitor those experts.</li>
        </ul>
        <p>Under Section 9 of the Uniform Prudent Investor Act, a trustee who prudently selects an agent, defines the scope of the delegation and periodically reviews the agent's performance generally is not liable under the Act for the agent's decisions. That is the type of protection a properly structured 3(38) relationship is designed to provide; its availability depends on applicable state law and the facts of each plan.</p>
      </div>
    </div>

    <!-- FAQ -->
    <div class="row mt-5">
      <div class="col-12">
        <h2>Retirement Plan Fiduciary FAQ</h2>
        <div class="accordion" id="retirementFaq">
          @for (faq of faqs; track faq.question; let i = $index) {
            <div class="accordion-item">
              <h3 class="accordion-header" [id]="'faqHeading' + i">
                <button class="accordion-button" [class.collapsed]="i !== 0" type="button" data-bs-toggle="collapse" [attr.data-bs-target]="'#faqCollapse' + i" [attr.aria-expanded]="i === 0" [attr.aria-controls]="'faqCollapse' + i">
                  {{ faq.question }}
                </button>
              </h3>
              <div [id]="'faqCollapse' + i" class="accordion-collapse collapse" [class.show]="i === 0" [attr.aria-labelledby]="'faqHeading' + i" data-bs-parent="#retirementFaq">
                <div class="accordion-body">{{ faq.answer }}</div>
              </div>
            </div>
          }
        </div>
      </div>
    </div>

    <!-- CTA -->
    <div class="row mt-5">
      <div class="col-12">
        <div class="cta-band text-center">
          <h2 class="mt-0">Start Your Fiduciary File Today</h2>
          <p>One of the most protective steps a committee can take is to begin documenting its governing documents, service agreements, meeting minutes and Investment Policy Statement. IFC can help you build that file and the plan behind it.</p>
          <a href="/open-account" class="btn btn-primary me-2 mb-2">Contact Us</a>
          <a href="tel:+18888155025" class="btn btn-outline-primary mb-2" aria-label="Call Investing for Catholics at 888.815.5025">Call 888-815-5025</a>
        </div>
      </div>
    </div>

    <!-- Disclosures -->
    <div class="row mt-5 disclosures">
      <div class="col-12">
        <h2>Disclosures</h2>
        <p>The discussion of laws, regulations, fiduciary standards and governance practices on this page is general in nature, is not legal, tax or ERISA advice, and may not apply to all plans or circumstances. Plan sponsors should consult qualified legal and tax professionals regarding their specific circumstances. Nothing on this page should be interpreted as a recommendation or determination of fiduciary status.</p>
        <p>Diversification does not guarantee a profit or protect against loss, and expected returns are not guaranteed. Target date portfolio allocations become more conservative over time, but they are not guaranteed, and investments remain subject to market risk, including near and after the target date. Fee information is available at <a routerLink="/fees">investingforcatholics.com/fees</a>.</p>
        <p>Advisory services are offered through Index Fund Advisors, Inc., a registered investment adviser, only pursuant to a written advisory agreement.</p>
      </div>
    </div>

  </div>
</section>